/**
 * WhoWeAreHorizonController — WHO WE ARE §2 "20 YEARS OF EXPERIENCE".
 *
 * 데스크톱(≥769 · 모션ON): __stage 를 핀 고정하고, 스크롤에 스크럽하여
 *   슬라이드를 하나씩 clip-path(inset 100%→0) 마스크로 아래→위 wipe 전환.
 *   (레퍼런스: views/template/components/slider/slider-pin-type1/index.js 의 clip 시퀀스)
 * 모바일/모션OFF: 아무것도 하지 않음 → SCSS 기본 세로 흐름 유지(그레이스풀).
 *
 * gsap.matchMedia 로 브레이크포인트 진입/이탈 시 자동 생성/정리 + ScrollTrigger.refresh.
 * Lenis↔ScrollTrigger 동기화는 SmoothScrollManager 소유(여기서 ticker/Lenis 생성 금지).
 *
 * ⚠ clip 시퀀스는 timeline({scrollTrigger}) 대신 ScrollTrigger.create + onUpdate 로 구동
 *   (signature 와 동일 패턴). gsap.timeline({scrollTrigger}) 는 번들 환경에서 플러그인
 *   프로퍼티 인식 실패 경고가 나므로 사용하지 않는다.
 */
import { getGsap, getScrollTrigger } from "../global/GsapGlobals";

const ROOT_SEL = '[data-section="wwa-horizon"]';

function clamp(v: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(v, hi));
}

export const WhoWeAreHorizonController = {
    init(): void {
        const root = document.querySelector<HTMLElement>(ROOT_SEL);
        if (!root) return;
        if (root.dataset.wwaHorizonBound === "true") return;
        root.dataset.wwaHorizonBound = "true";

        const stage = root.querySelector<HTMLElement>(".sec-wwa-horizon__stage");
        const slides = Array.from(
            root.querySelectorAll<HTMLElement>("[data-slide]"),
        );
        const dots = Array.from(root.querySelectorAll<HTMLElement>("[data-dot]"));
        if (!stage || slides.length < 2) return;

        const gsap = getGsap();
        const ScrollTrigger = getScrollTrigger();
        const total = slides.length;

        const setActiveDot = (idx: number): void => {
            dots.forEach((d, i) => d.classList.toggle("is-active", i === idx));
        };

        const mm = gsap.matchMedia();
        mm.add(
            "(min-width: 769px) and (prefers-reduced-motion: no-preference)",
            () => {
                root.classList.add("is-immersive");

                // 초기: 1번째 슬라이드만 노출, 나머지는 위에서 100% 클립(=숨김).
                slides.forEach((slide, i) => {
                    gsap.set(slide, {
                        clipPath: i === 0 ? "inset(0% 0 0 0)" : "inset(100% 0 0 0)",
                    });
                });
                setActiveDot(0);

                const st = ScrollTrigger.create({
                    trigger: stage,
                    start: "top top",
                    end: () => `+=${(total - 1) * window.innerHeight}`,
                    pin: true,
                    pinSpacing: true,
                    scrub: 1,
                    invalidateOnRefresh: true,
                    onUpdate: (self) => {
                        // seg ∈ [0, total-1]. 슬라이드 i 는 (i-1)~i 구간에서 위→아래 wipe.
                        const seg = self.progress * (total - 1);
                        slides.forEach((slide, i) => {
                            if (i === 0) return; // 항상 노출
                            const local = clamp(seg - (i - 1), 0, 1);
                            gsap.set(slide, {
                                clipPath: `inset(${(1 - local) * 100}% 0 0 0)`,
                            });
                        });
                        setActiveDot(Math.min(total - 1, Math.round(seg)));
                    },
                });

                return () => {
                    st.kill();
                    root.classList.remove("is-immersive");
                    slides.forEach((slide) =>
                        gsap.set(slide, { clearProps: "clipPath" }),
                    );
                    setActiveDot(0);
                };
            },
        );

        if (document.readyState === "complete") {
            ScrollTrigger.refresh();
        } else {
            window.addEventListener("load", () => ScrollTrigger.refresh(), {
                once: true,
            });
        }
    },
};
